/**
 * Pattern configuration registry — wallpaper groups, free/pro tiers, gallery
 * grouping, and system presets.
 *
 * A PatternConfig (system preset or user favourite) is the saveable unit: a
 * wallpaper group + lattice parameters. The tile shape is the group's
 * fundamental domain — there is no separate shape choice (see fundamental.ts).
 */
import type { SymmetryVariant } from '@/patterns/types';
import { defaultParams, type LatticeParams } from './engine';
import { buildFundamentalVariant, type ArtistTile } from './fundamental';
import {
  WALLPAPER_GROUPS,
  getWallpaperGroup,
  type RotationOrder,
  type WallpaperGroup,
  type WallpaperGroupId,
} from './groups';

export { PARAM_RANGES, defaultParams, normaliseParams } from './engine';
export type { LatticeParams, LatticeType } from './engine';
export { buildFundamentalVariant, rotationStepRadians } from './fundamental';
export type { ArtistTile } from './fundamental';
export { WALLPAPER_GROUPS, getWallpaperGroup } from './groups';
export type { RotationOrder, WallpaperGroup, WallpaperGroupId } from './groups';
export { WPG_THEMES, randomTheme, randomThemes } from './themes';
export type { WpgTheme } from './themes';
export {
  classifyColour,
  colourCountsFor,
  COLOUR_COUNTS,
  isValidColourCount,
} from './colourGroups';
export {
  BASE_PALETTE_SIZE,
  DEFAULT_COLOUR_INDEX,
  GRAPHITE_INDEX,
  nearestIndexForHex,
  paletteColourSet,
  paletteHex,
  paletteIndicesFor,
} from './palette';

// ── PatternConfig ────────────────────────────────────────────────────────────

export interface PatternConfig {
  id: string;
  /** user-chosen for favourites; fixed string for system presets */
  label: string;
  wpgId: WallpaperGroupId;
  latticeParams: LatticeParams;
  /** how the drawing tile is presented (special-case artist tiles) */
  artistTile: ArtistTile;
  /** true = baked in, not removable, not shown in the Favourites section */
  isSystem: boolean;
}

// ── Artist-shape grouping (sidebar display only) ─────────────────────────────

/**
 * The artist-convenient shape a group's fundamental domains assemble into,
 * used only to group the WPGs in the sidebar. (p3/p3m1/p31m sit under Triangle;
 * p6/p6m under Hexagon.)
 */
export type ArtistShapeId =
  | 'hexagon'
  | 'triangle'
  | 'square'
  | 'rectangle'
  | 'rhombus'
  | 'parallelogram'
  | 'assorted';

export const WPG_ARTIST_SHAPE: Record<WallpaperGroupId, ArtistShapeId> = {
  p1: 'parallelogram',
  p2: 'parallelogram',
  pm: 'rectangle',
  pg: 'rectangle',
  pmm: 'rectangle',
  pmg: 'rectangle',
  pgg: 'rectangle',
  cm: 'rhombus',
  cmm: 'rhombus',
  p4: 'square',
  p4m: 'square',
  p4g: 'square',
  p3: 'triangle',
  p3m1: 'triangle',
  p31m: 'triangle',
  p6: 'hexagon',
  p6m: 'hexagon',
};

/** Sidebar section order + labels. */
export const ARTIST_SHAPE_ORDER: { id: ArtistShapeId; label: string }[] = [
  { id: 'hexagon', label: 'Hexagon' },
  { id: 'triangle', label: 'Triangle' },
  { id: 'square', label: 'Square' },
  { id: 'rectangle', label: 'Rectangle' },
  { id: 'rhombus', label: 'Rhombus' },
  { id: 'parallelogram', label: 'Parallelogram' },
  { id: 'assorted', label: 'Assorted' },
];

const ARTIST_SHAPE_LABEL: Record<ArtistShapeId, string> = Object.fromEntries(
  ARTIST_SHAPE_ORDER.map((s) => [s.id, s.label]),
) as Record<ArtistShapeId, string>;

export function artistShapeFor(wpgId: WallpaperGroupId): ArtistShapeId {
  return WPG_ARTIST_SHAPE[wpgId];
}

// ── WPG gallery grouping by rotation order ───────────────────────────────────

export const ROTATION_SECTIONS: { order: RotationOrder; label: string }[] = [
  { order: 1, label: 'No rotation' },
  { order: 2, label: 'Half-turn symmetry' },
  { order: 3, label: 'Three-way symmetry' },
  { order: 4, label: 'Four-way symmetry' },
  { order: 6, label: 'Six-way symmetry' },
];

export function wpgsByRotation(): { label: string; groups: WallpaperGroup[] }[] {
  const all = Object.values(WALLPAPER_GROUPS);
  return ROTATION_SECTIONS.map(({ order, label }) => ({
    label,
    groups: all.filter((g) => g.rotationOrder === order),
  }));
}

// ── Tiers ────────────────────────────────────────────────────────────────────

export function isWpgFree(wpgId: WallpaperGroupId): boolean {
  return getWallpaperGroup(wpgId).free;
}

export const FREE_WPG_IDS: WallpaperGroupId[] = (Object.values(WALLPAPER_GROUPS) as WallpaperGroup[])
  .filter((g) => g.free)
  .map((g) => g.id);

// ── Build a renderable variant from a config ─────────────────────────────────

export function buildConfigVariant(config: PatternConfig): {
  polygon: [number, number][];
  variant: SymmetryVariant;
} {
  return buildFundamentalVariant(config.wpgId, config.latticeParams, config.artistTile);
}

/** Default params for a group, from its lattice. */
export function defaultLatticeParams(wpgId: WallpaperGroupId): LatticeParams {
  return defaultParams(getWallpaperGroup(wpgId).lattice);
}

// ── System presets ───────────────────────────────────────────────────────────

/** A system preset also carries its sidebar section (configs of the same group
 *  can appear in more than one section with different artist tiles). */
export interface SystemPreset extends PatternConfig {
  section: ArtistShapeId;
  /** true = not yet implemented; shown with "Coming soon" badge and disabled */
  deferred?: boolean;
}

function sys(
  id: string,
  label: string,
  wpgId: WallpaperGroupId,
  artistTile: ArtistTile,
  section: ArtistShapeId,
  deferred?: boolean,
): SystemPreset {
  return {
    id: `sys:${id}`,
    label,
    wpgId,
    latticeParams: defaultLatticeParams(wpgId),
    artistTile,
    isSystem: true,
    section,
    deferred,
  };
}

/**
 * Curated presets. The `section` is the actual *drawing-tile shape* (audited
 * from each variant's polygon), NOT the WPG's symmetry family — so e.g. p4's
 * triangular fundamental domain lives under Triangle, not Square. Tier follows
 * isWpgFree(wpgId) (free groups: p1, p3, p4, cm). p1/parallelogram aren't
 * offered as standalone presets (p1 is reachable via Configure); they appear
 * only as the hexagon/rhombus "Sliding" artist tiles.
 *
 * Hexagon artist tiles draw once per hexagon (neighbours rotated by class);
 * everything else draws in its fundamental domain.
 */
export const SYSTEM_PRESETS: SystemPreset[] = [
  // Hexagon — artist tile (one drawing per hexagon)
  sys('hex-p1', 'Sliding hex', 'p1', 'hexagon', 'hexagon'),
  sys('hex-p3', '3-way hex', 'p3', 'hexagon', 'hexagon'),
  // Triangle — fundamental domain is a triangle (audited)
  sys('tri-p4', 'Quarter turn', 'p4', 'standard', 'triangle'),
  sys('tri-cm', 'Diamond mirror', 'cm', 'standard', 'triangle'),
  sys('tri-p4m', 'Quarter mirror', 'p4m', 'standard', 'triangle'),
  sys('tri-p4g', 'Pinwheel mirror', 'p4g', 'standard', 'triangle'),
  sys('tri-p3m1', '3-way snowflake', 'p3m1', 'standard', 'triangle'),
  sys('tri-p6', '6-way triangle', 'p6', 'standard', 'triangle'),
  sys('tri-p6m', 'Snowflake', 'p6m', 'standard', 'triangle'),
  sys('tri-cmm', 'Diamond kaleidoscope', 'cmm', 'standard', 'triangle'),
  // Rectangle
  sys('rect-pm', 'Mirror', 'pm', 'standard', 'rectangle'),
  sys('rect-pg', 'Glide', 'pg', 'standard', 'rectangle'),
  sys('rect-pmm', 'Mirror grid', 'pmm', 'standard', 'rectangle'),
  sys('rect-pmg', 'Mirror & turn', 'pmg', 'standard', 'rectangle'),
  sys('rect-pgg', 'Double glide', 'pgg', 'standard', 'rectangle'),
  // Rhombus — the 120° rhombus (p3 cube face) + rhombus sliding
  sys('rho-p1', 'Sliding rhombus', 'p1', 'rhombus', 'rhombus'),
  sys('rho-p3', '3-way rhombus', 'p3', 'rhombus', 'rhombus'),
  // Parallelogram — the whole parallelogram as the drawing tile
  sys('para-p1', 'Sliding parallelogram', 'p1', 'standard', 'parallelogram'),
  sys('para-p2', 'Half-turn parallelogram', 'p2', 'parallelogram', 'parallelogram'),
  // Assorted 
  sys('assort-star', 'Three-way star', 'p31m', 'standard', 'assorted', false),
  sys('assort-cairo', 'Cairo pentagon', 'p4g', 'standard', 'assorted', true),
  sys('assort-floret', 'Floret pentagon', 'p6', 'standard', 'assorted', true),
];

export function getSystemPreset(id: string): PatternConfig | undefined {
  return SYSTEM_PRESETS.find((p) => p.id === id);
}

/** A concise parameter summary for the unsaved-config warning. */
export function configSummary(config: PatternConfig): string {
  const group = getWallpaperGroup(config.wpgId);
  const parts = [ARTIST_SHAPE_LABEL[artistShapeFor(config.wpgId)], group.label];
  if (config.latticeParams.aspectRatio !== undefined) {
    parts.push(`ratio ${config.latticeParams.aspectRatio.toFixed(2)}`);
  }
  if (config.latticeParams.angle !== undefined) {
    parts.push(`${Math.round(config.latticeParams.angle)}°`);
  }
  return parts.join(' · ');
}
